/*©**********************************************************************************
*                                                                                   *
* SPDX-License-Identifier: AGPL-3.0-or-later                                        *
*                                                                                   *
* This program is licensed under the GNU Affero General Public License v3+.         *
* See the LICENSE file or <https://www.gnu.org/licenses/agpl-3.0.html> for details. *
*                                                                                   *
**********************************************************************************©*/

/**
 * Auth utilities for JWT token management
 */

type StorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

const createMemoryStorage = (): StorageLike => {
  const store = new Map<string, string>();
  return {
    getItem: (key: string) => (store.has(key) ? store.get(key)! : null),
    setItem: (key: string, value: string) => {
      store.set(key, value);
    },
    removeItem: (key: string) => {
      store.delete(key);
    },
  };
};

const safeStorage: StorageLike =
  typeof window !== 'undefined' && window.localStorage
    ? window.localStorage
    : createMemoryStorage();

// Store token in localStorage
export const AUTH_SESSION_CHANGED = 'atp:auth-session-changed';

export const setAuthToken = (token: string): void => {
  safeStorage.setItem('auth_token', token);
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(AUTH_SESSION_CHANGED));
};

// Get token from localStorage
export const getAuthToken = (): string | null => {
  return safeStorage.getItem('auth_token');
};

export const getAuthTokenExpiryMs = (): number | null => {
  const token = getAuthToken();
  if (!token) return null;
  try {
    const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(encoded));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

// Remove token from localStorage
export const removeAuthToken = (): void => {
  safeStorage.removeItem('auth_token');
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(AUTH_SESSION_CHANGED));
};

export const endAuthSession = (): void => {
  // Keepalive lets the revocation reach the server when navigation follows.
  void fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/auth/logout`, {
    method: 'POST', credentials: 'include', keepalive: true,
  }).catch(() => undefined);
  removeAuthToken();
  safeStorage.removeItem('user');
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

// Create headers with authorization
export const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};
